import {
    createCustomSection,
    createEducationEntry,
    createExperienceEntry,
    createProjectEntry,
    displayField,
} from "./atsCvFormEngine.js";
import { mergeImportedAtsCvForm } from "./parseAtsCvFromText.js";

export const ATS_CV_IMPORT_MARKER_START = "@@SOHOZKAJ_ATS_CV_V1@@";
export const ATS_CV_IMPORT_MARKER_END = "@@SOHOZKAJ_ATS_CV_V1_END@@";

/**
 * @param {import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM} form
 */
export function serializeAtsCvFormForPdfImport(form) {
    return {
        v: 1,
        fullName: displayField(form.fullName),
        jobTitle: displayField(form.jobTitle),
        email: displayField(form.email),
        phone: displayField(form.phone),
        location: displayField(form.location),
        linkedin: displayField(form.linkedin),
        website: displayField(form.website),
        summary: displayField(form.summary),
        skills: typeof form.skills === "string" ? form.skills : "",
        certifications: typeof form.certifications === "string" ? form.certifications : "",
        languages: typeof form.languages === "string" ? form.languages : "",
        sectionTitles: form.sectionTitles || {},
        experiences: (form.experiences || []).map(
            ({ jobTitle, company, location, startDate, endDate, isCurrent, description }) => ({
                jobTitle: displayField(jobTitle),
                company: displayField(company),
                location: displayField(location),
                startDate: displayField(startDate),
                endDate: displayField(endDate),
                isCurrent: Boolean(isCurrent),
                description: displayField(description),
            })
        ),
        education: (form.education || []).map(
            ({ degree, institution, location, graduationYear, details }) => ({
                degree: displayField(degree),
                institution: displayField(institution),
                location: displayField(location),
                graduationYear: displayField(graduationYear),
                details: displayField(details),
            })
        ),
        projects: (form.projects || []).map(
            ({ name, link, technologies, date, description }) => ({
                name: displayField(name),
                link: displayField(link),
                technologies: displayField(technologies),
                date: displayField(date),
                description: displayField(description),
            })
        ),
        customSections: (form.customSections || []).map(
            ({ title, layout, content }) => ({
                title: displayField(title),
                layout: layout || "bullets",
                content: displayField(content),
            })
        ),
    };
}

/**
 * @param {ReturnType<typeof serializeAtsCvFormForPdfImport>} data
 */
export function deserializeAtsCvFormFromPdfImport(data) {
    if (!data || data.v !== 1) return null;

    const experiences = (data.experiences || []).map((exp, i) => ({
        ...createExperienceEntry(`imp-exp-${i}`),
        ...exp,
        id: `imp-exp-${i}`,
    }));

    const education = (data.education || []).map((edu, i) => ({
        ...createEducationEntry(`imp-edu-${i}`),
        ...edu,
        id: `imp-edu-${i}`,
    }));

    const projects = (data.projects || []).map((proj, i) => ({
        ...createProjectEntry(`imp-proj-${i}`),
        ...proj,
        id: `imp-proj-${i}`,
    }));

    const customSections = (data.customSections || []).map((sec, i) => ({
        ...createCustomSection(`imp-custom-${i}`),
        ...sec,
        id: `imp-custom-${i}`,
    }));

    return mergeImportedAtsCvForm({
        fullName: data.fullName,
        jobTitle: data.jobTitle,
        email: data.email,
        phone: data.phone,
        location: data.location,
        linkedin: data.linkedin,
        website: data.website,
        summary: data.summary,
        skills: data.skills,
        certifications: data.certifications,
        languages: data.languages,
        sectionTitles: data.sectionTitles,
        experiences,
        education,
        projects,
        customSections,
    });
}

function encodePayloadJson(obj) {
    const json = JSON.stringify(obj);
    if (typeof TextEncoder !== "undefined") {
        const bytes = new TextEncoder().encode(json);
        let binary = "";
        bytes.forEach((b) => {
            binary += String.fromCharCode(b);
        });
        return btoa(binary);
    }
    return btoa(unescape(encodeURIComponent(json)));
}

function decodePayloadJson(b64) {
    const compact = String(b64 || "").replace(/\s+/g, "");
    if (!compact) return null;
    try {
        if (typeof TextDecoder !== "undefined") {
            const binary = atob(compact);
            const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
            return JSON.parse(new TextDecoder().decode(bytes));
        }
        return JSON.parse(decodeURIComponent(escape(atob(compact))));
    } catch {
        return null;
    }
}

/** Safe max length per PDF info field (bytes); split payload if larger. */
const PDF_INFO_FIELD_MAX = 28000;

/**
 * Store import payload in PDF document info only (no visible text on pages).
 * @param {import("jspdf").jsPDF} pdf
 * @param {import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM} form
 */
export function embedAtsCvImportPayloadInPdf(pdf, form) {
    const payload = encodePayloadJson(serializeAtsCvFormForPdfImport(form));
    const embedded = `${ATS_CV_IMPORT_MARKER_START}${payload}${ATS_CV_IMPORT_MARKER_END}`;

    const base = {
        title: form.fullName ? `${form.fullName} - CV` : "CV",
        author: form.fullName || "Sohozkaj",
        creator: "Sohozkaj ATS CV Maker",
    };

    if (embedded.length <= PDF_INFO_FIELD_MAX) {
        pdf.setProperties({
            ...base,
            subject: embedded,
            keywords: "ATS CV, Sohozkaj",
        });
        return;
    }

    const mid = Math.floor(payload.length / 2);
    pdf.setProperties({
        ...base,
        subject: `${ATS_CV_IMPORT_MARKER_START}${payload.slice(0, mid)}`,
        keywords: `${payload.slice(mid)}${ATS_CV_IMPORT_MARKER_END}`,
    });
}

/**
 * @param {string} text
 * @returns {import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM | null}
 */
export function extractAtsCvFormFromCompactText(text) {
    const compact = String(text || "").replace(/\s+/g, "");
    const start = compact.indexOf(ATS_CV_IMPORT_MARKER_START);
    const end = compact.indexOf(ATS_CV_IMPORT_MARKER_END);
    if (start < 0 || end < 0 || end <= start) return null;

    const b64 = compact.slice(start + ATS_CV_IMPORT_MARKER_START.length, end);
    const data = decodePayloadJson(b64);
    return deserializeAtsCvFormFromPdfImport(data);
}

/**
 * @param {string[]} lines
 * @returns {import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM | null}
 */
export function extractAtsCvFormFromImportLines(lines) {
    const fromJoined = extractAtsCvFormFromCompactText(lines.join("\n"));
    if (fromJoined) return fromJoined;
    return extractAtsCvFormFromCompactText(lines.map((l) => l.trim()).join(""));
}

/**
 * @param {import("pdfjs-dist").PDFDocumentProxy} pdf
 * @returns {Promise<import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM | null>}
 */
export async function extractAtsCvFormFromPdfMetadata(pdf) {
    try {
        const meta = await pdf.getMetadata();
        const info = meta?.info || {};
        const combined = ["Subject", "Keywords", "Creator", "Producer", "Title"]
            .map((key) => info[key])
            .filter(Boolean)
            .join("");
        return extractAtsCvFormFromCompactText(combined);
    } catch {
        return null;
    }
}
